import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const MemberCard = ({ member, onSelect, isSelected }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
        return 'bg-success';
      case 'away':
        return 'bg-warning';
      default:
        return 'bg-muted';
    }
  };

  const getClassIcon = (className) => {
    const classIcons = {
      'Warrior': 'Sword',
      'Cleric': 'Heart',
      'Rogue': 'Dagger',
      'Wizard': 'Wand2',
      'Ranger': 'Target',
      'Paladin': 'Shield',
      'Necromancer': 'Skull',
      'Enchanter': 'Sparkles',
      'Magician': 'Flame',
      'Bard': 'Music'
    };
    return classIcons?.[className] || 'User';
  };

  return (
    <div className="card transition-base hover:shadow-warm-md">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4 flex-1">
          <div className="relative">
            <Image
              src={member?.avatar}
              alt={member?.avatarAlt}
              className="w-16 h-16 md:w-20 md:h-20 rounded-xl object-cover"
            />
            <div className={`absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-card ${getStatusColor(member?.status)}`} />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg md:text-xl font-semibold text-foreground truncate">
                {member?.name}
              </h3>
              {member?.isOfficer && (
                <div className="role-badge officer flex-shrink-0">
                  <Icon name="Shield" size={14} />
                  <span>Officer</span>
                </div>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Joined {member?.joinDate}
            </p>
            <div className="flex items-center gap-4 mt-2 text-xs md:text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Icon name="Users" size={14} />
                {member?.characters?.length} Characters
              </span>
              <span className="flex items-center gap-1">
                <Icon name="TrendingUp" size={14} />
                {member?.activityLevel}% Active
              </span>
            </div>
          </div>
        </div>

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-2 rounded-lg hover:bg-muted transition-base flex-shrink-0"
          aria-label={isExpanded ? 'Collapse characters' : 'Expand characters'}
        >
          <Icon 
            name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
            size={20} 
            color="var(--color-foreground)" 
          />
        </button>
      </div>
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-border space-y-3">
          <h4 className="text-sm font-medium text-foreground mb-3">Characters</h4>
          {member?.characters?.map((character) => (
            <div
              key={character?.id}
              className="flex items-center gap-3 p-3 rounded-lg bg-muted hover:bg-primary/10 transition-base"
            >
              <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center flex-shrink-0">
                <Icon 
                  name={getClassIcon(character?.class)} 
                  size={20} 
                  color="var(--color-accent-foreground)" 
                />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-medium text-foreground truncate">
                    {character?.name}
                  </p>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    Lvl {character?.level}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {character?.class} • {character?.specialization}
                </p>
                <div className="mt-1 w-full h-1 bg-background rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-success transition-all duration-250"
                    style={{ width: `${character?.progression}%` }}
                  />
                </div>
              </div>

              <div className="text-right flex-shrink-0">
                <p className="text-xs text-muted-foreground">
                  {character?.lastActive}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
      {member?.isOfficer && (
        <div className="mt-4 pt-4 border-t border-border">
          <button
            onClick={() => onSelect(member?.id)}
            className={`w-full px-4 py-2 rounded-lg text-sm font-medium transition-base ${
              isSelected
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-foreground hover:bg-primary/20'
            }`}
          >
            {isSelected ? (
              <span className="flex items-center justify-center gap-2">
                <Icon name="Check" size={16} />
                Selected
              </span>
            ) : (
              'Select for Messaging'
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default MemberCard;